import { SvgComponentKey } from '@/assets/admin';

type EmptyMessage = {
  title: string;
  message?: string;
  icon: SvgComponentKey;
};

export const EMPTY_MESSAGES = {
  messages: {
    default: {
      title: 'No conversations found',
      message: 'Start a new conversation with your residents or check other filter tabs.',
      icon: 'EMPTY_MESSAGE' as const,
    },
    archived: {
      title: 'No archived messages',
      message: 'You have no archived messages at the moment',
      icon: 'THUMB_UP' as const,
    },
    open: {
      title: 'No open conversations',
      message: 'You have no open conversations at the moment',
      icon: 'THUMB_UP' as const,
    },
    inbox: {
      title: 'No assigned conversations',
      message: 'You have no conversations assigned to you',
      icon: 'THUMB_UP' as const,
    },
    noResults: (query: string): EmptyMessage => ({
      title: 'No conversations found',
      message: `No conversations match your search "${query}". Try a different search term.`,
      icon: 'EMPTY_MESSAGE' as const,
    }),
  },
  bulletins: {
    feed: {
      Scheduled: {
        title: 'No scheduled bulletins',
        message: 'There are no bulletins scheduled for future delivery. Create a new bulletin and schedule it for later.',
        icon: 'MESSAGE_BLAST' as const,
      },
      Draft: {
        title: 'No draft bulletins',
        message: 'There are no bulletins saved as drafts. Start creating a new bulletin and save it as a draft to continue later.',
        icon: 'MESSAGE_BLAST' as const,
      },
      Default: {
        title: 'Create new bulletin',
        message: "Keep your audience informed and engaged. Click the 'New bulletin' button to create one.",
        icon: 'MESSAGE_BLAST' as const,
      },
    },
    noResults: (query: string): EmptyMessage => ({
      title: 'No bulletins found',
      message: `No bulletins match your search "${query}". Try a different search term.`,
      icon: 'MESSAGE_BLAST' as const,
    }),
  },
  events: {
    default: {
      title: 'Create your first event',
      message: 'Start organizing community events and activities. Create events, manage RSVPs, and track attendance all in one place.',
      icon: 'CALENDAR' as const,
    },
    noResults: (query: string): EmptyMessage => ({
      title: 'No events found',
      message: `No events match your search "${query}". Try a different search term.`,
      icon: 'CALENDAR' as const,
    }),
    desktopNoEvents: {
      title: 'Create new event',
      message: 'Take the first step towards creating the upcoming event with ease.',
      icon: 'CALENDAR_EVENT' as const,
    },
  },
  tasks: {
    default: {
      title: 'Create your first task',
      message: 'Stay organized and track your work. Use the quick add feature or create detailed tasks to get started.',
      icon: 'FOLDER' as const,
    },
    withFilters: {
      title: 'No tasks match your filters',
      message: 'Try adjusting your search or filters to find tasks.',
      icon: 'FOLDER' as const,
    },
    archivedDefault: {
      title: 'No archived tasks found',
      message: 'Archived tasks will appear here when you archive completed or cancelled tasks.',
      icon: 'FOLDER' as const,
    },
    archivedWithFilters: {
      title: 'No archived tasks match your filters',
      message: 'Try adjusting your search or filters to find archived tasks.',
      icon: 'FOLDER' as const,
    },
    archivedForMonth: (monthYear: string): EmptyMessage => ({
      title: `No archived tasks for ${monthYear}`,
      message: 'Try selecting a different month or adjusting your filters.',
      icon: 'FOLDER' as const,
    }),
  },
  resources: {
    default: {
      title: 'Create new resource',
      message: "Keep your residents informed with helpful resources. Click the 'New resource' button to create one.",
      icon: 'FOLDER' as const,
    },
  },
  residents: {
    default: {
      title: 'No residents found',
      message: 'Please verify property is properly configured.',
      icon: 'newUser' as any,
    },
    noResults: (query: string): EmptyMessage => ({
      title: 'No residents found',
      message: `No residents match your search "${query}". Try a different search term.`,
      icon: 'newUser' as any,
    }),
  },
  userManagement: {
    default: {
      title: 'No users found',
      message: 'Try adjusting filters or add a new user.',
      icon: 'EMPTY_MESSAGE' as const,
    },
    noResults: (query: string): EmptyMessage => ({
      title: 'No users found',
      message: `No users match your search "${query}". Try a different term or filters.`,
      icon: 'EMPTY_MESSAGE' as const,
    }),
  },
  properties: {
    default: {
      title: 'No properties found',
      message: 'Create your first property to get started.',
      icon: 'PROPERTY' as const,
    },
    noResults: (query: string): EmptyMessage => ({
      title: 'No properties found',
      message: `No properties match your search "${query}". Try a different search term.`,
      icon: 'PROPERTY' as const,
    }),
  },
  default: {
    title: 'No data found',
    message: 'No data found',
    icon: 'EMPTY_MESSAGE' as any,
  },
} as const;

export type EmptyMessagesConfig = typeof EMPTY_MESSAGES;

